/**
 * Wire DTOs — request/response shapes shared between client and server.
 *
 * IMPORTANT: BigInt fields are sent as strings (`xp`, `crowns`) because BigInt
 * doesn't survive JSON.stringify natively, and we want lossless precision.
 * The client should treat them as opaque strings unless arithmetic is needed
 * — in which case use the BigInt constructor on the client too.
 */

import type {
  AccountRole,
  BattleStat,
  CharacterStatus,
  CrimeCategory,
  CrimeOutcome,
  GoodDeedCategory,
  GoodDeedOutcome,
  ItemCategory,
  ItemTier,
} from './enums.js';

// ──────────────────────────────────────────────────────────────────────────────
// AUTH
// ──────────────────────────────────────────────────────────────────────────────

export interface RegisterRequest {
  email: string;
  password: string;
  characterName: string;
  /** Must be true — server rejects registration without explicit acceptance. */
  acceptedTerms: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthResponse {
  accessToken: string;
  // Refresh token is delivered via httpOnly cookie, NOT in the body.
  // The client never sees it directly.
  account: AccountSummary;
  character: CharacterDto;
}

export interface AccountSummary {
  id: string;
  email: string;
  role: AccountRole;
  emailVerified: boolean;
  /** Permanent registration index — "Soul #N" founder badge. Null for the
   *  system/seed account (and absent on minimal auth responses; the full value
   *  always arrives via /me). */
  soulNumber?: number | null;
  /** True if the account has accepted the latest Rules + TOS version. */
  termsCurrent?: boolean;
  /** The current Rules + TOS version the server expects acceptance of. */
  termsVersion?: string;
  /** Active membership tier slug ('squire' | 'knight' | 'baron') or null when free. */
  membershipTier?: string | null;
  /** ISO timestamp the current membership expires. Null if free. */
  membershipExpiresAt?: string | null;
  /** Flair colour for the membership badge. */
  membershipFlairColour?: string | null;
}

// ──────────────────────────────────────────────────────────────────────────────
// CHARACTER
// ──────────────────────────────────────────────────────────────────────────────

export interface CharacterDto {
  id: string;
  accountId: string;
  name: string;

  level: number;
  xp: string; // BigInt as string
  /** XP earned within the current level, and XP still needed to reach the next.
   *  Both BigInt-as-string. Drives the Profile progress-to-next-level bar. */
  xpIntoLevel: string;
  xpToNextLevel: string;

  strength: number;
  defense: number;
  speed: number;
  dexterity: number;

  toil: number;
  wits: number;
  endurance: number;

  vigor: number;
  vigorMax: number;
  resolve: number;
  resolveMax: number;
  spirit: number;
  spiritMax: number;
  life: number;
  lifeMax: number;

  crowns: string; // BigInt as string
  tokens: number;
  /** Per-bar refill-token bucket — earned passively (1/day each, banked while
   *  away) plus membership deposits; spend one for a full bar refill. */
  vigorTokens: number;
  resolveTokens: number;
  spiritTokens: number;

  infamy: number;
  cunning: number;
  /** Resolve-max permanently granted by the crime-grind ladder (see Misdeeds). */
  crimeResolveBonus: number;
  /** Resolve-max permanently granted by the good-deed ladder (see Good Deeds). */
  goodDeedResolveBonus: number;
  /** Good-Deeds gate/skill (mirror of Cunning) — gates greater deeds + improves odds. */
  virtue: number;
  /** Good-Deeds reputation (mirror of Infamy) — drives Awards & Titles. */
  honour: number;
  boons: number;

  /** Total successful revives — the Leechcraft XP total (drives revive Vigor
   *  cost + the prestige level/bar on the Profile and Infirmary). */
  leechcraftLevel: number;
  /** Total successful breakouts — the Gaolcraft XP total (drives breakout
   *  Resolve cost + the prestige level/bar on the Profile and Dungeon). */
  gaolcraftLevel: number;

  status: CharacterStatus;
  statusUntil: string | null; // ISO 8601
  location: string;

  /** ISO timestamp when new-arrival PvP protection ends (createdAt + the
   *  configured window). Null once the account is older than that — drives the
   *  "New Arrival" profile badge. */
  protectionEndsAt: string | null;

  /** Active displayed sub-title slug (null = no title). References the title
   *  catalog. NOT a level-rank — those are auto-derived; see levelRankSlug. */
  activeTitleSlug: string | null;
  /** Auto-derived Level Rank slug (never null — defaults to 'adventurer').
   *  Computed server-side from character.level via getLevelRank(). */
  levelRankSlug: string;
  /** All avatars the player has uploaded (base64 data URLs, max 5). */
  avatarLibrary: string[];
  /** Currently displayed avatar (one of avatarLibrary), or null for default. */
  activeAvatarUrl: string | null;

  /** Up to 3 award slugs the player has pinned to their profile. */
  pinnedAwardSlugs: string[];

  /** Tutorial progress (0 = not started, 1-5 = step, 99 = done). */
  tutorialStep: number;

  /** Item cooldown expiries, ISO 8601 strings or null when no cooldown is active. */
  elixirCooldownUntil: string | null;
  tonicCooldownUntil: string | null;
  medicalCooldownUntil: string | null;
  foodCooldownUntil: string | null;

  /** Active ship slug — null when in the Coracle. References the ship catalog. */
  shipSlug: string | null;

  lastRegenAt: string; // ISO 8601 — useful so the client can predict the next tick
}

/** House bloodchain snapshot for the sidebar chain tracker. The chain is
 *  HOUSE-WIDE: every PvP WIN any member lands against a character target inside
 *  the rolling window feeds it. The client re-derives the live count, the bonus
 *  the next win will earn, and the cold-out countdown from `recentWins` + the
 *  tuning constants, ticking each second without re-polling. Null when the
 *  player is not in a House. */
export interface HouseChainDto {
  /** Rolling window length in seconds (used by the per-step Renown bonus). */
  windowSeconds: number;
  /** Renown added per chain step. */
  perStep: number;
  /** Maximum chain bonus (the cap). */
  maxBonus: number;
  /** ISO timestamps of the House's qualifying wins still inside the window,
   *  newest first (bounded). */
  recentWins: string[];
  /** True monotonic streak count — the visible chain on the sidebar. Only
   *  resets to 0 when no House member has landed a win for
   *  `chainResetSeconds`. Won't backslide on the rolling-window edge. */
  streakCount: number;
  /** ISO time of the most-recent House WIN; client uses it to render a
   *  countdown to streak reset. Null when there has never been a House win. */
  streakLastAt: string | null;
  /** Seconds of inactivity before the streak resets to 0. */
  chainResetSeconds: number;
  /** Streak milestones that grant one-shot Renown bonuses when the chain
   *  lands exactly on the count. Sorted ascending by `at`. */
  milestones: Array<{ at: number; bonus: number }>;
}

export interface MeResponse {
  account: AccountSummary;
  character: CharacterDto;
  /** Bank interest schedule for the sidebar wallet countdown. Null = no bank yet.
   *  `toWallet` is true when the vault is full (interest overflows to wallet);
   *  false while it still compounds into the bank. */
  bankInterest?: { nextInterestAt: string; perDayCrowns: string; toWallet: boolean } | null;
  /** House bloodchain snapshot, or null when not in a House. */
  chain?: HouseChainDto | null;
  /** The Shade's mark on the viewer, polled live so the client can pop the
   *  "a Shade stalks you" modal the moment it fixes on them. Carries the
   *  active wound too, so every page can show the stat cut. */
  darkRaid?: { marked: boolean; windowEndsAt: string | null; wound?: { pct: number; healsAt: string } | null };
  /** Individually-granted awards the player hasn't accepted yet — surfaced so
   *  the client can pop the award modal. */
  pendingAwards?: PendingAwardDto[];
}

/** A one-off award awaiting the player's acceptance (shown as a pop-up). */
export interface PendingAwardDto {
  id: string;
  title: string;
  body: string;
  /** Human-readable reward lines, e.g. "+100 Vigor (max)", "+250,000 Crowns". */
  rewards: string[];
}

export interface AcceptAwardResponse {
  character: CharacterDto;
  /** The reward lines that were just applied. */
  rewards: string[];
}

// ──────────────────────────────────────────────────────────────────────────────
// PUBLIC PROFILE — the read-only "scouting card" shown when you view another
// player (e.g. by clicking a name in the Annals). Deliberately a SUBSET: no
// crowns/bank balance, no inventory, no email — just what rivals may see.
// ──────────────────────────────────────────────────────────────────────────────

export interface PublicProfileAwardDto {
  slug: string;
  name: string;
  achievedAt: string | null;
}

export interface PublicProfileDto {
  id: string;
  name: string;
  level: number;
  /** Active displayed sub-title slug (null = none). */
  activeTitleSlug: string | null;
  /** Auto-derived level-rank slug (never null). */
  levelRankSlug: string;
  activeAvatarUrl: string | null;
  status: CharacterStatus;
  location: string;

  // Battle stats (public).
  strength: number;
  defense: number;
  speed: number;
  dexterity: number;

  // Reputation (public).
  infamy: number;
  cunning: number;
  /** Good-Deeds gate/skill (mirror of Cunning). */
  virtue: number;
  /** Good-Deeds reputation (mirror of Infamy). */
  honour: number;

  /** House membership, or null if unaffiliated. */
  house: { id: string; name: string; tag: string; rank: HouseRank } | null;

  /** Achieved awards only (locked ones are not exposed publicly). */
  awards: PublicProfileAwardDto[];
  /** Up to 3 award slugs the player pinned to their profile. */
  pinnedAwardSlugs: string[];

  /** Account/character creation date, for the "member since" line. */
  joinedAt: string | null;
  /** ISO timestamp when new-arrival PvP protection ends (createdAt + the
   *  configured window). Null once protection has lapsed — drives the
   *  "New Arrival" profile badge. */
  protectionEndsAt: string | null;
  /** When the character was last active — bumped on every action and regen
   *  tick (Character.updatedAt). Drives the "Last active …" line. */
  lastActiveAt: string | null;
  /** Permanent registration index — "Soul #N" founder badge. Null if unassigned. */
  soulNumber: number | null;

  /** Active membership tier slug ('squire'|'knight'|'baron'), or null if free /
   *  expired. Drives the public name colour + membership pill. */
  membershipTier?: string | null;
  /** Flair colour for the membership name/pill, or null when not a member. */
  membershipFlairColour?: string | null;

  /** Active Shade-wound, if any — every battle stat is cut by `pct` % in
   *  combat until `healsAt`. Public so rivals can see the weakness. */
  wound?: { pct: number; healsAt: string } | null;
}

export interface PublicProfileResponse {
  profile: PublicProfileDto;
}

// ──────────────────────────────────────────────────────────────────────────────
// TRAINING
// ──────────────────────────────────────────────────────────────────────────────

export interface TrainRequest {
  stat: BattleStat;
  vigor: number;
  /** Optional training tier slug. Defaults to 'tyro_yard'. */
  tier?: string;
}

export interface TrainResponse {
  character: CharacterDto;
  stat: BattleStat;
  /** How much the trained stat increased by. */
  gain: number;
  vigorSpent: number;
  spiritDrained: number;
  xpGained: number;
  levelsGained: number;
}

export interface TrainingTierDto {
  slug: string;
  name: string;
  blurb: string;
  /** Minimum stat (across STR/DEF/SPE/DEX) required to use this tier. */
  unlockMinStat: number;
  /** Multiplier on per-train gain. */
  gainMultiplier: number;
  /** Multiplier on vigor cost (purely informational — server still uses literal vigor amount). */
  vigorCostMultiplier: number;
  unlocked: boolean;
}

export interface TrainingTiersResponse {
  tiers: TrainingTierDto[];
}

// ──────────────────────────────────────────────────────────────────────────────
// CRIMES
// ──────────────────────────────────────────────────────────────────────────────

export interface CrimeListing {
  key: string;
  category: CrimeCategory;
  name: string;
  blurb: string;
  resolveCost: number;
  cunningRequired: number;
  /** Crown range on TRIUMPH (display only). */
  crowns: [number, number];
  /** Cunning granted on a TRIUMPH (half on PARTIAL). Display only. */
  cunningOnSuccess: number;
  /** Infamy granted on a TRIUMPH (half on PARTIAL). Display only. */
  infamyOnSuccess: number;
  /** Whether this crime can drop items at all (for the UI hint). */
  canDropItems: boolean;
  /** Overall item-drop chance on success, 0..1 (display as a %). */
  dropChance: number;
  /** High Schemes: crowns staked up-front. Triumph returns the stake plus the
   *  `crowns` profit; Partial returns the stake alone; Foiled and Disastrous
   *  forfeit it. 0 for ordinary misdeeds. */
  stakeCrowns: number;
  /** Weighted item drop table — one entry per possible drop. The client
   *  computes per-item % as (entry.weight / sum(weight)). Empty when the
   *  crime can't drop items at all. */
  dropTable: LootTableEntryDto[];
  /** Material pool tier this crime rolls from on TRIUMPH. */
  materialPoolTier: MaterialPoolTierDto;
  /** Chance (0..1) that the material roll fires on TRIUMPH. */
  materialDropChance: number;
  /** Weighted material drop table for `materialPoolTier`. */
  materialDropTable: LootTableEntryDto[];
  /**
   * Base chance of a DISASTROUS outcome → the Dungeon, 0..1 (display as a %).
   * The crime mirror of a Good Deed's injuryChance → Infirmary. Static base
   * value; the live roll is reduced by the character's Cunning.
   */
  disastrousChance: number;
  /** How many times this player has attempted THIS crime inside the
   *  diminishing-returns window (0 = fresh). */
  repeatsInWindow: number;
  /** Crown multiplier the NEXT attempt on this crime would earn from
   *  diminishing returns (1 = full payout, down to the floor). */
  crownMultiplier: number;
  /** ISO time the fatigue on THIS crime fully clears (windowMinutes after the
   *  most recent attempt). Null when there's no active fatigue. */
  repeatResetAt: string | null;
}

/** One row in a weighted loot table. Quantity is a range; the actual roll
 *  picks uniformly from [qtyMin, qtyMax]. */
export interface LootTableEntryDto {
  itemSlug: string;
  itemName: string;
  weight: number;
  qtyMin: number;
  qtyMax: number;
}

/** Mirrors the server's MaterialPoolTier — used for the loot popup label. */
export type MaterialPoolTierDto =
  | 'crude'
  | 'common'
  | 'fine'
  | 'masterwork'
  | 'legendary';

export interface CrimesIndexResponse {
  crimes: CrimeListing[];
  /** Fractional Resolve-cost reduction the player currently enjoys on misdeeds
   *  (0 = none; 0.10 with the Cunning Writs perk). The client applies
   *  Math.ceil(cost*(1-pct)) — so 1-Resolve misdeeds stay at 1. */
  resolveDiscountPct: number;
  /** Trailing window (minutes) over which repeating the same crime diminishes
   *  its crown payout. Different crimes have independent counters. */
  repeatWindowMinutes: number;
  /** Per-category mastery, keyed by category slug. */
  mastery: Record<string, CategoryMasteryDto>;
}

/** Mastery in one deed category — earned by TRIUMPHS in that category. Each
 *  level grants a Resolve-cost discount and a drop-chance bonus on every deed
 *  of the category, plus an epithet. */
export interface CategoryMasteryDto {
  /** 0..5. */
  level: number;
  /** Lifetime TRIUMPH count in this category. */
  triumphs: number;
  /** Triumphs needed for the next level (null at max). */
  nextAt: number | null;
  /** Epithet for the current level ('' at level 0). */
  epithet: string;
  /** Fractional resolve discount this level grants on the category. */
  resolveDiscountPct: number;
  /** Additive drop-chance bonus this level grants on the category. */
  dropChanceBonus: number;
}

export interface AttemptCrimeRequest {
  crimeKey: string;
}

export interface AttemptCrimeResponse {
  character: CharacterDto;
  crimeKey: string;
  crimeName: string;
  outcome: CrimeOutcome;
  resolveSpent: number;
  /** BigInt as string. */
  crownsDelta: string;
  cunningDelta: number;
  infamyDelta: number;
  xpDelta: number;
  /** True if this attempt pushed the character up at least one level. */
  leveledUp: boolean;
  itemDropped: { slug: string; name: string; qty: number } | null;
  /** Forge materials looted on TRIUMPH. Independent of itemDropped. */
  materialsDropped: Array<{ slug: string; name: string; qty: number }>;
  /** ISO 8601 — set when DISASTROUS sent the player to the Dungeon. */
  jailedUntil: string | null;
  /** Themed mishap line for a FOILED or DISASTROUS attempt, else null. */
  failureBlurb: string | null;
  /** Prior attempts on this same crime in the DR window BEFORE this one. */
  repeatsInWindow: number;
  /** Crown multiplier diminishing returns applied to THIS attempt (1 = full). */
  crownMultiplier: number;
}

// ──────────────────────────────────────────────────────────────────────────────
// GOOD DEEDS — the lawful mirror of crimes.
// ──────────────────────────────────────────────────────────────────────────────

export interface GoodDeedListing {
  key: string;
  category: GoodDeedCategory;
  name: string;
  blurb: string;
  resolveCost: number;
  /** Virtue gate (mirror of cunningRequired). */
  virtueRequired: number;
  /** Crown range on TRIUMPH (display only — already ×crownTierRatio). */
  crowns: [number, number];
  /** Virtue granted on a TRIUMPH (half on PARTIAL). Display only. */
  virtueOnSuccess: number;
  /** Honour granted on a TRIUMPH (half on PARTIAL). Display only. */
  honourOnSuccess: number;
  canDropItems: boolean;
  /** Overall gift-drop chance on success, 0..1 (display as a %). */
  dropChance: number;
  /** Great Undertakings: crowns endowed up-front. Triumph returns the
   *  endowment plus the `crowns` reward; Partial returns it alone; an
   *  Unfulfilled attempt forfeits it. 0 for ordinary deeds. */
  stakeCrowns: number;
  /** Weighted gift drop table — one entry per possible gift the grateful
   *  folk might press into your hands. Empty when the deed can't drop. */
  dropTable: LootTableEntryDto[];
  /** Chance, on a FOILED deed, of being injured (Infirmary), 0..1. Display as %. */
  injuryChance: number;
  /** How many times this player has attempted THIS deed inside the
   *  diminishing-returns window (0 = fresh). */
  repeatsInWindow: number;
  /** Crown multiplier the NEXT attempt on this deed would earn from
   *  diminishing returns (1 = full payout, down to the floor). */
  crownMultiplier: number;
  /** ISO time the fatigue on THIS deed fully clears (windowMinutes after the
   *  most recent attempt). Null when there's no active fatigue. */
  repeatResetAt: string | null;
}

export interface GoodDeedLedgerDto {
  attempts: number;
  triumphs: number;
  partials: number;
  foiled: number;
  /** BigInt as string. */
  crownsEarned: string;
  virtueEarned: number;
  honourEarned: number;
}

export interface GoodDeedsIndexResponse {
  deeds: GoodDeedListing[];
  ledger: GoodDeedLedgerDto;
  /** Fractional Resolve-cost reduction the player currently enjoys on good deeds
   *  (0 = none; 0.10 with the Pious Writs perk). The client applies
   *  Math.ceil(cost*(1-pct)) — so 1-Resolve deeds stay at 1. */
  resolveDiscountPct: number;
  /** Trailing window (minutes) over which repeating the same deed diminishes
   *  its crown payout. Different deeds have independent counters. */
  repeatWindowMinutes: number;
  /** Per-category mastery, keyed by category slug. */
  mastery: Record<string, CategoryMasteryDto>;
}

export interface AttemptGoodDeedResponse {
  character: CharacterDto;
  deedKey: string;
  deedName: string;
  outcome: GoodDeedOutcome;
  resolveSpent: number;
  /** BigInt as string. */
  crownsDelta: string;
  virtueDelta: number;
  honourDelta: number;
  xpDelta: number;
  /** True if this attempt pushed the character up at least one level. */
  leveledUp: boolean;
  itemDropped: { slug: string; name: string; qty: number } | null;
  /** True if a FOILED deed injured the character (sent to Infirmary). */
  injured: boolean;
  /** ISO time the Infirmary stay ends, or null if not injured. */
  injuredUntil: string | null;
  /** Themed mishap line for a FOILED deed (shown whether or not injured). */
  failureBlurb: string | null;
  /** Prior attempts on this same deed in the DR window BEFORE this one. */
  repeatsInWindow: number;
  /** Crown multiplier diminishing returns applied to THIS attempt (1 = full). */
  crownMultiplier: number;
}

// ──────────────────────────────────────────────────────────────────────────────
// GRAND SCHEMES / GREAT WORKS — multi-stage endgame chains (v1.13C).
// ──────────────────────────────────────────────────────────────────────────────

export type ChainKind = 'scheme' | 'work';

/** Chain outcomes reuse the deed bands; DISASTROUS occurs on schemes only. */
export type ChainAttemptOutcome = 'TRIUMPH' | 'PARTIAL' | 'FOILED' | 'DISASTROUS';

export interface ChainStepDto {
  key: string;
  name: string;
  blurb: string;
  /** Cunning (scheme) or Virtue (work) soft floor. */
  gate: number;
  resolveCost: number;
  /** Crowns charged up-front; spent on TRIUMPH/FOILED, refunded on PARTIAL. */
  expenseCrowns: number;
  /** Hours the next step stays locked after this one triumphs. */
  waitHoursAfter: number;
}

export interface ChainProgressDto {
  /** Next step index to attempt (0-based). */
  step: number;
  /** ISO time the next attempt becomes available. */
  stepReadyAt: string;
  completions: number;
  collapses: number;
}

export interface ChainDto {
  key: string;
  kind: ChainKind;
  name: string;
  blurb: string;
  epigraph: string;
  steps: ChainStepDto[];
  /** Hours a FOILED step bars the chain. */
  setbackHours: number;
  /** Hours before a fresh run may begin after completion or collapse. */
  restartHours: number;
  /** Crown purse on completion (display range). */
  completionCrowns: [number, number];
  chestName: string;
  trophyName: string;
  /** Cunning+Infamy (scheme) / Virtue+Honour (work) granted on completion. */
  progressGain: number;
  progress: ChainProgressDto;
}

export interface ChainsIndexResponse {
  chains: ChainDto[];
}

export interface AttemptChainRequest {
  chainKey: string;
}

export interface AttemptChainResponse {
  character: CharacterDto;
  chainKey: string;
  stepKey: string;
  stepName: string;
  outcome: ChainAttemptOutcome;
  resolveSpent: number;
  /** Net crown movement of this attempt (expense and/or purse). BigInt string. */
  crownsDelta: string;
  /** Next step index after this attempt resolved. */
  stepAfter: number;
  /** ISO time the chain may next be attempted. */
  stepReadyAt: string;
  /** True when this attempt finished the final step. */
  completed: boolean;
  /** True when a DISASTROUS roll collapsed the scheme back to nothing. */
  collapsed: boolean;
  reward: {
    /** BigInt string. */
    crowns: string;
    chest: { slug: string; name: string };
    trophy: { slug: string; name: string };
    progressGain: number;
  } | null;
  /** Themed mishap line for FOILED / collapse, else null. */
  failureBlurb: string | null;
  /** ISO 8601 — set when a collapse sent the player to the Dungeon. */
  jailedUntil: string | null;
}

// ──────────────────────────────────────────────────────────────────────────────
// THE WANDERING CARAVAN — daily crafting commissions + Seal shop.
// ──────────────────────────────────────────────────────────────────────────────

export interface CaravanCommissionDto {
  /** Craft family: smith | alchemist | cook | brewer. */
  family: string;
  /** Display station name, e.g. "The Forge". */
  station: string;
  itemSlug: string;
  itemName: string;
  tier: string;
  qty: number;
  /** How many of the item the player currently holds. */
  held: number;
  rewardSeals: number;
  fulfilledAt: string | null;
  fulfillable: boolean;
  fulfilled: boolean;
}

export interface CaravanWareDto {
  slug: string;
  name: string;
  blurb: string;
  costSeals: number;
  kind: string;
  /** One-time wares already owned (e.g. the titles, the Star). */
  owned: boolean;
  affordable: boolean;
  /** Max purchases per day (0 = unlimited). */
  dailyLimit: number;
  /** True when today's purchases of this ware have hit dailyLimit. */
  atDailyLimit: boolean;
  /** For item-kind wares: the granted item's slug, so the name can open the
   *  item-info popup (as in inventory). Absent for title/boon/refill wares. */
  itemSlug?: string;
}

export interface CaravanIndexResponse {
  date: string;
  seals: number;
  commissions: CaravanCommissionDto[];
  wares: CaravanWareDto[];
}

export interface FulfilCommissionResponse {
  family: string;
  itemName: string;
  qty: number;
  sealsEarned: number;
  sealsBalance: number;
}

export interface BuyWareRequest {
  wareSlug: string;
}

export interface BuyWareResponse {
  wareName: string;
  sealsSpent: number;
  sealsBalance: number;
  granted: string;
}

// ──────────────────────────────────────────────────────────────────────────────
// INVENTORY
// ──────────────────────────────────────────────────────────────────────────────

export interface InventoryItemDto {
  slug: string;
  name: string;
  category: ItemCategory;
  tier: ItemTier;
  blurb: string;
  qty: number;
  baseValue: number;
  acquiredAt: string;
  stackable: boolean;
  /** Combat stats from the catalog, present only on gear (weapons/armour).
   *  Used to show "atk/acc/def" inside the equip-slot dropdowns. */
  attack?: number;
  accuracy?: number;
  defense?: number;
  /** True if this item has a usable effect (consumable). Drives the Use button. */
  usable?: boolean;
  /** Cooldown bucket the item's effect uses, if any. */
  cooldownClass?: 'elixir' | 'tonic' | 'medical' | 'food' | null;
  /** True if using it clears the Infirmary status. */
  removesInfirmary?: boolean;
  /** True if using it clears the Dungeon status. */
  removesDungeon?: boolean;
}

export interface InventoryResponse {
  items: InventoryItemDto[];
}

// ──────────────────────────────────────────────────────────────────────────────
// ITEM USE
// ──────────────────────────────────────────────────────────────────────────────

export interface UseItemRequest {
  itemSlug: string;
}

export interface UseItemResponse {
  character: CharacterDto;
  itemSlug: string;
  itemName: string;
  /** Did an overdose trigger? If so, restored values are zero and you've been hospitalised. */
  overdosed: boolean;
  /** What the item actually restored, post-cap. */
  restored: { vigor: number; resolve: number; spirit: number; life: number };
  /** Status removed by this use, if any. */
  removedStatus: 'INFIRMARY' | 'DUNGEON' | null;
  /** Cooldown expiry timestamp, ISO 8601. */
  cooldownUntil: string;
  /** Seconds shaved off an Infirmary sentence by a medical item (0 if none). */
  infirmaryReducedSeconds: number;
  /** Non-fatal notices (e.g. overfill cap reached, excess wasted). */
  warnings: string[];
}

// ──────────────────────────────────────────────────────────────────────────────
// DUNGEON
// ──────────────────────────────────────────────────────────────────────────────

export interface BustResponse {
  character: CharacterDto;
  success: boolean;
  /** Probability the server actually rolled against (transparency). */
  chance: number;
  /** On failure, dungeon time was extended by this many seconds. */
  extensionSeconds: number;
}

export interface BailResponse {
  character: CharacterDto;
  /** Crowns spent on bail (BigInt as string). */
  cost: string;
}

// ──────────────────────────────────────────────────────────────────────────────
// RESCUE — freeing other players from the Dungeon / Infirmary.
// ──────────────────────────────────────────────────────────────────────────────

/** A character currently held in the Dungeon or Infirmary, shown in the rescue list. */
export interface OccupantDto {
  characterId: string;
  name: string;
  level: number;
  /** ISO 8601 — when their status timer ends. */
  statusUntil: string | null;
  /** Active displayed title slug, for flair. Null if none. */
  activeTitleSlug: string | null;
}

export interface OccupantsResponse {
  occupants: OccupantDto[];
  /** Resolve the requesting player would spend to break someone out RIGHT NOW
   *  (falls with Gaolcraft level). */
  breakoutResolveCost: number;
  /** Vigor the requesting player would spend to revive someone RIGHT NOW
   *  (falls with Leechcraft level). */
  reviveVigorCost: number;
  /** The requesting player's current Gaolcraft level (each breakout +1). */
  gaolcraftLevel: number;
  /** The requesting player's current Leechcraft level (each revive +1). */
  leechcraftLevel: number;
}

export interface BreakoutResponse {
  /** The rescuer's updated character (Resolve spent). */
  character: CharacterDto;
  /** Name of the freed prisoner. */
  freedName: string;
  /** Resolve the rescuer spent. */
  resolveSpent: number;
  /** Gaolcraft level AFTER this breakout (each success raises it). */
  gaolcraftLevel: number;
}

export interface ReviveResponse {
  /** The rescuer's updated character (Vigor spent). */
  character: CharacterDto;
  /** Name of the revived patient. */
  revivedName: string;
  /** Vigor the rescuer poured in. */
  vigorSpent: number;
  /** Leechcraft level AFTER this revive (each success raises it). */
  leechcraftLevel: number;
}

// ──────────────────────────────────────────────────────────────────────────────
// SCHOLARIUM (Education)
// ──────────────────────────────────────────────────────────────────────────────

export type CourseTier =
  | 'novice'
  | 'apprentice'
  | 'master'
  | 'royal'
  | 'vigil'
  | 'season'
  | 'pilgrimage'
  | 'consecration'
  | 'apotheosis'
  | 'empyrean';

export interface LegacyEffectDto {
  vigorMax: number;
  resolveMax: number;
  spiritMax: number;
  lifeMax: number;
}

export interface CourseListing {
  key: string;
  tier: CourseTier;
  name: string;
  blurb: string;
  durationSeconds: number;
  costCrowns: number;
  /** Working-stat gains on completion. */
  gains: { toil: number; wits: number; endurance: number };
  /** XP awarded on every claim (repeats included for repeatable tiers). */
  xpReward: number;
  /** Perk granted on first completion (slug only — UI may render a label later). */
  perkSlug: string | null;
  /** Permanent cap raise, granted ONCE (first completion). All zeros = no legacy (short courses). */
  legacy: LegacyEffectDto;
  /**
   * True when this course can be undertaken only once per character
   * (Pilgrimage / Consecration / Apotheosis). The enrolment endpoint refuses
   * a second enrolment once the first claim has been made.
   */
  oneShot: boolean;
  /**
   * For one-shot courses: true if the viewer has already completed it and is
   * therefore locked out of re-enrolment. Always false for repeatable tiers.
   */
  completed: boolean;
}

export interface CoursesResponse {
  courses: CourseListing[];
}

export interface ActiveEnrollmentDto {
  id: string;
  courseKey: string;
  courseName: string;
  startedAt: string;
  completesAt: string;
  ready: boolean;
}

export interface EnrollRequest {
  courseKey: string;
}

export interface EnrollResponse {
  character: CharacterDto;
  courseKey: string;
  courseName: string;
  startedAt: string;
  completesAt: string;
}

export interface ClaimCourseRequest {
  enrollmentId: string;
}

export interface ClaimCourseResponse {
  character: CharacterDto;
  courseKey: string;
  courseName: string;
  gains: { toil: number; wits: number; endurance: number };
  /** null if no perk; alreadyOwned=true if the perk had been granted before. */
  perk: { slug: string; alreadyOwned: boolean } | null;
  /** Legacy raises applied this claim (zeros if course has no legacy, or already earned). */
  legacy: LegacyEffectDto;
  /** True when this course's Legacy cap raise was withheld because the player
   *  has already earned it (it grants only on the first completion). */
  legacyCapped?: boolean;
  /** XP awarded this claim. */
  xpGained: number;
  /** Levels gained from that XP (0 if none). */
  levelsGained: number;
}

export interface ScholariumStatusResponse {
  active: ActiveEnrollmentDto | null;
}

/** One owned perk and, for legacy perks, how far its stacking has progressed.
 *  Name + effect text are resolved client-side from PERK_CATALOG by slug. */
export interface PerkProgressDto {
  slug: string;
  /** true = legacy (stat-cap raise that stacks up to `max` completions). */
  legacy: boolean;
  /** Completions counted toward the legacy cap (0 for one-time perks). */
  completions: number;
  /** The legacy stacking cap (LEGACY_MAX_STACKS_PER_COURSE); 0 for one-time. */
  max: number;
}

export interface ScholariumPerksResponse {
  perks: PerkProgressDto[];
}

// ──────────────────────────────────────────────────────────────────────────────
// VOCATIONS (Trades)
// ──────────────────────────────────────────────────────────────────────────────

export interface TradeListing {
  key: string;
  name: string;
  blurb: string;
  /** Tier band 1-6 — drives the client's tier chip/colour and grouping. */
  tier: number;
  requirements: { toil: number; wits: number; endurance: number };
  /** Base wage before any scaling — what a player at exactly the requirement earns. */
  wageCrowns: number;
  /**
   * Wage the *viewing* player would actually earn per claim if they took this
   * trade right now, given their current working stats. Computed server-side
   * using `wageCrowns × sqrt(stat / requirement)` with multi-stat trades
   * pinned to the bottleneck ratio. Equals `wageCrowns` if the player has
   * exactly the requirement; grows from there.
   */
  effectiveWage: number;
  /** Multiplier applied: effectiveWage / wageCrowns. 1.0 at requirement; higher above. */
  wageMultiplier: number;
  gains: { toil: number; wits: number; endurance: number };
}

export interface TradesResponse {
  trades: TradeListing[];
  /** Server-configured wage cycle in seconds (UI uses for hint text). */
  cycleSeconds: number;
}

export interface ActiveVocationDto {
  tradeKey: string;
  tradeName: string;
  rank: number;
  joinedAt: string;
  lastClaimedAt: string;
  totalEarned: string;
  nextClaimAt: string;
  ready: boolean;
}

export interface JoinTradeRequest {
  tradeKey: string;
}

export interface JoinTradeResponse {
  character: CharacterDto;
  tradeKey: string;
  tradeName: string;
  joinedAt: string;
  nextClaimAt: string;
}

export interface ClaimWagesResponse {
  character: CharacterDto;
  tradeKey: string;
  tradeName: string;
  /** BigInt as string. */
  wagesPaid: string;
  gains: { toil: number; wits: number; endurance: number };
  nextClaimAt: string;
}

export interface VocationStatusResponse {
  active: ActiveVocationDto | null;
}

// ──────────────────────────────────────────────────────────────────────────────
// MARKET (Merchant's Market)
// ──────────────────────────────────────────────────────────────────────────────

export type MarketListingStatus = 'ACTIVE' | 'SOLD' | 'CANCELLED' | 'EXPIRED';

export interface MarketListingDto {
  id: string;
  itemSlug: string;
  itemName: string;
  itemCategory: ItemCategory;
  itemTier: ItemTier;
  qty: number;
  qtyOriginal: number;
  pricePer: string;        // BigInt as string
  totalIfBuyAll: string;   // BigInt as string
  status: MarketListingStatus;
  listedAt: string;
  expiresAt: string;
  closedAt: string | null;
  sellerId: string;
  sellerName: string;
}

export interface MarketSearchResponse {
  listings: MarketListingDto[];
}

export interface ListItemRequest {
  itemSlug: string;
  qty: number;
  /** BigInt as string. */
  pricePer: string;
  /** Optional, defaults to server's MARKET.defaultListingSeconds. */
  durationSeconds?: number;
}

export interface ListItemResponse {
  listing: MarketListingDto;
}

export interface BuyListingRequest {
  listingId: string;
  qty: number;
}

export interface BuyListingResponse {
  character: CharacterDto;
  listingId: string;
  itemSlug: string;
  itemName: string;
  qty: number;
  pricePer: string;
  totalPaid: string;
  listingRemaining: number;
}

export interface CancelListingRequest {
  listingId: string;
}

export interface CancelListingResponse {
  character: CharacterDto;
}

export interface MyListingsResponse {
  listings: MarketListingDto[];
}

// ──────────────────────────────────────────────────────────────────────────────
// TRAVEL & SHIPS
// ──────────────────────────────────────────────────────────────────────────────

export type TravelPhase = 'home' | 'outbound' | 'at_kingdom' | 'inbound';

export interface KingdomShopItemDto {
  itemSlug: string;
  name: string;
  category: ItemCategory;
  tier: ItemTier;
  blurb: string;
  price: number;
  /** Shared remaining stock for this city item (0..stockMax). */
  stock: number;
  /** Ceiling the stock refills toward each hour. */
  stockMax: number;
}

export interface KingdomDto {
  slug: string;
  name: string;
  blurb: string;
  /** The region this destination belongs to (e.g. 'caledonia'). */
  region: string;
  /** Region display name (e.g. 'Caledonia'). */
  regionName: string;
  /** Travel time in seconds with the player's ship/perks applied. */
  travelTimeSeconds: number;
  shop?: KingdomShopItemDto[];
  /** How many times this character has arrived at this kingdom (0 = never).
   *  Drives the "visited N times" badge and the at-home shop reveal. */
  visitCount?: number;
  /** Gathering trades plied at this city, each with its unique named spot
   *  (e.g. Fishing at "the black mirror of Loch Clyde"). Only populated once
   *  the character has visited the city at least once. */
  trades?: KingdomTradeDto[];
}

export interface KingdomTradeDto {
  /** Trade display name ("Fishing", "Pearl-Diving", …). */
  skill: string;
  /** The city's unique named gathering spot for that trade. */
  spotName: string;
}

export interface TripDto {
  fromKingdom: string;
  toKingdom: string;
  departedAt: string;
  arrivesAt: string;
  itemsBought: number;
  carryCapacity: number;
}

export interface TravelStatusResponse {
  phase: TravelPhase;
  character: CharacterDto;
  trip: TripDto | null;
  currentKingdom: KingdomDto | null;
  kingdoms: KingdomDto[];
}

export interface DepartRequest {
  toKingdom: string;
}

export interface DepartResponse {
  character: CharacterDto;
  trip: TripDto;
}

export interface BuyShopRequest {
  itemSlug: string;
  qty: number;
}

export interface BuyShopResponse {
  character: CharacterDto;
  itemSlug: string;
  itemName: string;
  qty: number;
  totalSpent: string;
  itemsBought: number;
  carryCapacity: number;
  /** Shared stock left for this item after the purchase. */
  stockRemaining: number;
}

export interface ShipDto {
  slug: string;
  name: string;
  blurb: string;
  speedMultiplier: number;
  costCrowns: number;
  /** Extra cargo slots added to carryCapacity for every trip. */
  carryBonus: number;
  /** % boost to cunning rolls on crimes. */
  crimeBoostPercent: number;
  /** % boost to listing price on foreign-only items at the home market. */
  marketResaleBoostPercent: number;
  /** One-line description of this ship's unique perk. */
  perkBlurb: string;
  /** True if the player owns this ship (in their fleet). */
  owned: boolean;
  /** True if this ship is currently being sailed. */
  active: boolean;
  /** ISO date the ship was acquired, or null if not owned. */
  acquiredAt: string | null;
}

export interface ShipsResponse {
  ships: ShipDto[];
}

export interface BuyShipRequest {
  shipSlug: string;
}

export interface SwitchShipRequest {
  shipSlug: string;
}

export interface SwitchShipResponse {
  character: CharacterDto;
  shipSlug: string;
  shipName: string;
}

export interface BuyShipResponse {
  character: CharacterDto;
  shipSlug: string;
  shipName: string;
  costPaid: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// BANK / COUNTING HOUSE
// ─────────────────────────────────────────────────────────────────────────────

export interface BankDto {
  tierSlug: string;
  tierName: string;
  tierBlurb: string;
  /** Crowns currently held in the vault (BigInt wire-string). */
  principalCrowns: string;
  /** Maximum crowns this tier can hold (BigInt wire-string). */
  capCrowns: string;
  /** Raw rate-per-second (rarely needed by UI; ratePerDayPct is friendlier). */
  ratePerSecond: number;
  /** Daily interest rate as a percentage (e.g. 1.5 = 1.5%/day). */
  ratePerDayPct: number;
  /** The daily interest lump paid to the wallet at this principal (BigInt wire-string). */
  interestPerDayCrowns: string;
  /** True if principal is at or above the tier cap. */
  capacityFull: boolean;
  /** When the next daily interest lump is paid into the wallet (ISO 8601). */
  nextInterestAt: string;
}

export interface BankTierDto {
  slug: string;
  name: string;
  blurb: string;
  capCrowns: string;
  ratePerDayPct: number;
  upgradeCostCrowns: string;
  /** True if this is the player's currently-active tier. */
  active: boolean;
  /** True if this tier is the next upgrade step available. */
  upgradeable: boolean;
  /** True if the player has reached this tier or earlier. */
  owned: boolean;
}

export interface BankStateResponse {
  character: CharacterDto;
  bank: BankDto;
  tiers: BankTierDto[];
}

export interface BankDepositRequest {
  /** Amount of crowns to move from wallet to bank (BigInt wire-string). */
  amount: string;
}

export interface BankWithdrawRequest {
  /** Amount of crowns to move from bank back to wallet (BigInt wire-string). */
  amount: string;
}

export interface BankUpgradeRequest {
  /** Target tier slug — must be exactly one rung above current. */
  toTierSlug: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIL
// ─────────────────────────────────────────────────────────────────────────────

export type MailFolder = 'inbox' | 'sent';

export interface MailSummaryDto {
  id: string;
  /** True for outbound mail (you sent it); false for inbound. */
  fromMe: boolean;
  /** Display name of the OTHER party. "System" for system mail. */
  otherName: string;
  /** Character id of the other party (or null for system). */
  otherId: string | null;
  subject: string;
  /** Truncated preview of the body. */
  preview: string;
  sentAt: string;
  readAt: string | null;
}

export interface MailDetailDto extends MailSummaryDto {
  body: string;
}

export interface MailListResponse {
  folder: MailFolder;
  mails: MailSummaryDto[];
  unreadCount: number;
  total: number;
}

export interface MailDetailResponse {
  mail: MailDetailDto;
}

export interface SendMailRequest {
  toHandle: string;
  subject: string;
  body: string;
}

export interface SendMailResponse {
  mailId: string;
  recipientName: string;
}

export interface MailUnreadCountResponse {
  unreadCount: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// NOTIFICATIONS
// ─────────────────────────────────────────────────────────────────────────────

export type NotificationKind =
  | 'mail'
  | 'pvp_attack'
  | 'market_sale'
  | 'expedition_ready'
  | 'war_declared'
  | 'holding_lost'
  | 'holding_won'
  | 'revived'   // someone pulled you out of the Infirmary
  | 'freed'     // someone broke you out of the Dungeon
  | 'crypt_reward' // a Crypt boss fell — spoils are waiting
  | 'dark_raid'    // the Shade has marked you
  | 'system';

export interface NotificationDto {
  id: string;
  kind: NotificationKind;
  message: string;
  link: string | null;
  createdAt: string;
  readAt: string | null;
}

export interface NotificationListResponse {
  notifications: NotificationDto[];
  unreadCount: number;
}

export interface NotificationUnreadCountResponse {
  unreadCount: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// SETTINGS
// ─────────────────────────────────────────────────────────────────────────────

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// CROWN TRANSFER (Send Coin — peer-to-peer wallet gift)
// ─────────────────────────────────────────────────────────────────────────────

export interface SendCrownsRequest {
  /** Recipient handle (= character name). */
  toHandle: string;
  /** Amount of crowns to send (BigInt wire-string). */
  amount: string;
}

export interface SendCrownsResponse {
  /** Sender's updated character (wallet debited). */
  character: CharacterDto;
  recipientName: string;
  /** Amount actually sent (BigInt wire-string). */
  amountSent: string;
  /** Crowns the sender may still transfer today, after this send (wire-string). */
  dailyRemaining: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// LEADERBOARDS
// ─────────────────────────────────────────────────────────────────────────────

export type LeaderboardType = 'players' | 'houses';
export type LeaderboardFormat = 'int' | 'big' | 'float';

export interface LeaderboardMetricDto {
  slug: string;
  label: string;
  format: LeaderboardFormat;
}

export interface LeaderboardMetricsResponse {
  players: LeaderboardMetricDto[];
  houses: LeaderboardMetricDto[];
}

export interface LeaderboardEntryDto {
  rank: number;
  name: string;
  handle: string;
  value: string;
  meta: string | null;
  /** Player's active title slug (null for House rows or untitled players). */
  activeTitleSlug?: string | null;
  /** Character id for player rows (null/absent for House rows) — used to deep-link
   *  to the public profile from the Annals. */
  characterId?: string | null;
  /** House id for House rows (null/absent for player rows) — deep-links to the
   *  public house view from the Annals. */
  houseId?: string | null;
  /** House banner data URL for House rows — drives the small banner thumb in
   *  the Annals. Null/absent for player rows or houses with no banner. */
  bannerUrl?: string | null;
  /** Flair colour for active members — tints the name in the Annals. Null/absent
   *  for free accounts and House rows. */
  membershipFlairColour?: string | null;
}

export interface LeaderboardResponse {
  type: LeaderboardType;
  metric: string;
  metricLabel: string;
  metricFormat: LeaderboardFormat;
  entries: LeaderboardEntryDto[];
  total: number;
  limit: number;
  offset: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// CAREER STATS
// ─────────────────────────────────────────────────────────────────────────────

export interface CareerStatsDto {
  crimes: {
    attempts: number;
    triumphs: number;
    partials: number;
    foiled: number;
    disastrous: number;
    crownsEarned: string;
    /** Total Infamy gained through crime (mirrors goodDeeds.honourEarned). */
    infamyEarned: number;
  };
  goodDeeds: {
    attempts: number;
    triumphs: number;
    partials: number;
    /** 'FOILED' outcome — the deed went unfulfilled. */
    unfulfilled: number;
    crownsEarned: string;
    honourEarned: number;
  };
  combat: {
    fightsInitiated: number;
    wins: number;
    losses: number;
    draws: number;
    escaped: number;
    totalDamageDealt: number;
    totalDamageReceived: number;
    xpFromCombat: number;
  };
  market: {
    itemsSold: number;
    crownsEarnedFromSales: string;
    crownsPaidInFees: string;
    itemsBought: number;
    crownsSpentBuying: string;
  };
  awardsCount: number;
  expeditionsClaimed: number;
  /** Whole-fortune net worth (wallet + bank + every owned asset), BigInt
   *  wire-string. Same reckoning as the Annals wealth ledger. */
  netWorth: string;
}

export interface CareerStatsResponse {
  stats: CareerStatsDto;
}

// ─────────────────────────────────────────────────────────────────────────────
// ACTIVITY FEED
// ─────────────────────────────────────────────────────────────────────────────

export type ActivityKind =
  | 'crime'
  | 'attack_initiated'
  | 'attack_received'
  | 'market_sold'
  | 'market_bought'
  | 'award_earned'
  | 'expedition_claimed';

export interface ActivityEntryDto {
  id: string;
  kind: ActivityKind;
  at: string;
  message: string;
  detail: string | null;
}

export interface ActivityResponse {
  entries: ActivityEntryDto[];
}

// ─────────────────────────────────────────────────────────────────────────────
// FRIENDS + BLOCKS
// ─────────────────────────────────────────────────────────────────────────────

export interface FriendEntryDto {
  /** Row id, used to remove. */
  id: string;
  characterId: string;
  name: string;
  level: number;
  status: string;
  /** True if the other party has also added you. */
  mutual: boolean;
  addedAt: string;
}

export interface FriendsListResponse {
  friends: FriendEntryDto[];
}

export interface BlocksListResponse {
  blocks: FriendEntryDto[];
}

export interface EnemiesListResponse {
  enemies: FriendEntryDto[];
}

export interface AddEnemyResponse {
  enemy: FriendEntryDto;
}

export interface AddFriendRequest {
  handle: string;
}

export interface AddFriendResponse {
  friend: FriendEntryDto;
}

export interface AddBlockResponse {
  block: FriendEntryDto;
}

// ─────────────────────────────────────────────────────────────────────────────
// DAILY QUESTS
// ─────────────────────────────────────────────────────────────────────────────

export interface QuestDto {
  slug: string;
  name: string;
  blurb: string;
  progress: number;
  target: number;
  /** BigInt wire-string. */
  rewardCrowns: string;
  rewardBoons: number;
  rewardXp: number;
  claimedAt: string | null;
  claimable: boolean;
  complete: boolean;
}

export interface TodayQuestsResponse {
  /** UTC YYYY-MM-DD. */
  date: string;
  quests: QuestDto[];
}

export interface ClaimQuestResponse {
  quest: QuestDto;
  paid: {
    crowns: string;
    boons: number;
    xp: number;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// ALMSGIVING — daily curio-donation offers (the Almshouse board)
// ─────────────────────────────────────────────────────────────────────────────

export interface DonationDto {
  itemSlug: string;
  itemName: string;
  /** Item tier (crude…mythic) — drives the rarity styling. */
  tier: string;
  /** Keepers-voice charitable ask. */
  blurb: string;
  /** How many of the curio must be donated. */
  qty: number;
  /** How many the player currently holds. */
  held: number;
  /** BigInt wire-string. */
  rewardCrowns: string;
  rewardXp: number;
  rewardBoons: number;
  fulfilledAt: string | null;
  /** held >= qty && !fulfilledAt. */
  fulfillable: boolean;
  fulfilled: boolean;
}

export interface TodayDonationsResponse {
  /** UTC YYYY-MM-DD. */
  date: string;
  donations: DonationDto[];
}

export interface FulfilDonationResponse {
  donation: DonationDto;
  paid: {
    crowns: string;
    boons: number;
    xp: number;
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// DOMICILE
// ──────────────────────────────────────────────────────────────────────────────

export interface DomicileTierDto {
  slug: string;
  name: string;
  blurb: string;
  costCrowns: number;
  spiritBonus: number;
  upgradeSlots: number;
}

export interface DomicileUpgradeDto {
  slug: string;
  name: string;
  blurb: string;
  costCrowns: number;
  spiritBonus: number;
  effects: {
    travelSpeedBonusPct?: number;
    wageBonusPct?: number;
    elixirCooldownReduceSeconds?: number;
    infirmaryReducePct?: number;
    crownStorage?: boolean;
    garrisonDefense?: boolean;
  };
}

export interface DomicileFurnishingDto {
  /** Crafted-ware item slug this furnishing places. */
  wareSlug: string;
  name: string;
  blurb: string;
  spiritBonus: number;
  effects: {
    travelSpeedBonusPct?: number;
    wageBonusPct?: number;
    elixirCooldownReduceSeconds?: number;
    infirmaryReducePct?: number;
    crownStorage?: boolean;
    garrisonDefense?: boolean;
  };
}

export interface DomicileCatalogueResponse {
  tiers: DomicileTierDto[];
  upgrades: DomicileUpgradeDto[];
  furnishings: DomicileFurnishingDto[];
}

export interface OwnedDomicileDto {
  tier: string;
  tierName: string;
  acquiredAt: string;
  upgradeSlots: number;
  upgrades: Array<{ slug: string; name: string; installedAt: string }>;
  spiritBonus: number;
  slotsUsed: number;
  slotsRemaining: number;
  /** Furnishing slot pool (separate from upgrade slots). */
  furnishingSlots: number;
  furnishings: Array<{ wareSlug: string; name: string; installedAt: string }>;
  furnishingSlotsUsed: number;
  furnishingSlotsRemaining: number;
}

export interface FurnishingRequest {
  wareSlug: string;
}

export interface FurnishingResponse {
  character: CharacterDto;
  domicile: OwnedDomicileDto;
}

export interface MyDomicileResponse {
  domicile: OwnedDomicileDto | null;
}

export interface BuyDomicileRequest {
  tier: string;
}

export interface BuyDomicileResponse {
  character: CharacterDto;
  domicile: OwnedDomicileDto;
  costPaid: string;
  refundReceived: string;
}

export interface SellDomicileResponse {
  character: CharacterDto;
  refundReceived: string;
}

export interface UpgradeRequest {
  upgradeSlug: string;
}

export interface UpgradeResponse {
  character: CharacterDto;
  domicile: OwnedDomicileDto;
  costPaid: string;
}

export interface UninstallUpgradeResponse {
  character: CharacterDto;
  domicile: OwnedDomicileDto;
  refundReceived: string;
}

// ──────────────────────────────────────────────────────────────────────────────
// COMBAT
// ──────────────────────────────────────────────────────────────────────────────

export type EquipSlot =
  | 'primary' | 'secondary' | 'helm' | 'chest' | 'amulet'
  | 'rod' | 'spear' | 'pickaxe' | 'axe' | 'basket';

export interface LoadoutSlotDto {
  slot: EquipSlot;
  itemSlug: string | null;
  itemName: string | null;
  attack: number;
  accuracy: number;
  defense: number;
}

export interface LoadoutDto {
  primary: LoadoutSlotDto;
  secondary: LoadoutSlotDto;
  helm: LoadoutSlotDto;
  chest: LoadoutSlotDto;
  amulet: LoadoutSlotDto;
  /** Gathering tool slots (non-combat). */
  rod: LoadoutSlotDto;
  spear: LoadoutSlotDto;
  pickaxe: LoadoutSlotDto;
  axe: LoadoutSlotDto;
  basket: LoadoutSlotDto;
  totalWeaponAttack: number;
  totalWeaponAccuracy: number;
  totalArmor: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// GATHERING — vigor-based field professions (Phase 1: Fishing)
// ─────────────────────────────────────────────────────────────────────────────

export interface GatheringSkillProgressDto {
  level: number;
  /** BigInt wire-string. */
  xp: string;
  xpIntoLevel: number;
  /** XP span of the current level, or null past the soft cap. */
  xpForNextLevel: number | null;
}

export interface CatchPreviewDto {
  slug: string;
  name: string;
  tier: string;
  minLevel: number;
  /** True if the player's skill level is below minLevel (can't catch it yet). */
  locked: boolean;
  /** Relative weight in the catch table (drives the pop-out odds display). */
  weight: number;
  /** Quantity range a single successful attempt yields. */
  qtyMin: number;
  qtyMax: number;
}

export interface GatheringActivityDto {
  skillKey: string;
  skillName: string;
  actionVerb: string;
  /** Verb for the "What you might ___" odds button ("catch", "mine", "fell"). */
  takeVerb: string;
  toolNoun: string;
  spotName: string;
  spotBlurb: string;
  vigorPerAttempt: number;
  maxVigorPerAction: number;
  skill: GatheringSkillProgressDto;
  tool: { slug: string; name: string; power: number } | null;
  catchable: CatchPreviewDto[];
}

export interface GatheringStateResponse {
  citySlug: string;
  vigorNow: number;
  /** One entry per gathering skill available at the current city. */
  activities: GatheringActivityDto[];
}

export interface CatchLineDto {
  slug: string;
  name: string;
  tier: string;
  qty: number;
}

export interface GatherResponse {
  character: CharacterDto;
  skillKey: string;
  skillName: string;
  spotName: string;
  attempts: number;
  vigorSpent: number;
  catches: CatchLineDto[];
  skillXpGained: number;
  skillLevelBefore: number;
  skillLevelAfter: number;
  globalXpGained: number;
  levelsGained: number;
}

/** One row per gathering trade for the profile readout (all trades, any location). */
export interface GatheringSkillSummaryDto {
  skillKey: string;
  name: string;
  actionVerb: string;
  /** Plural noun for the lifetime tally ("fish & catch landed", "ore & stone hewn"). */
  yieldNoun: string;
  /** True for the five tooled trades; false for the bare-handed labour trades. */
  hasToolSlot: boolean;
  toolEquipped: { slug: string; name: string; power: number } | null;
  level: number;
  xp: string;
  xpIntoLevel: number;
  xpForNextLevel: number | null;
  totalCatch: string;
  blurb: string;
}

export interface GatheringSkillsResponse {
  skills: GatheringSkillSummaryDto[];
}

export interface NpcDto {
  slug: string;
  name: string;
  blurb: string;
  /** Minimum battle stat (lowest of STR/DEF/SPE/DEX) required to challenge. */
  unlockMinStat: number;
  strength: number;
  defense: number;
  speed: number;
  dexterity: number;
  life: number;
  weaponSlug: string | null;
  xpOnWin: number;
  crownsOnWin: number;
  /** Cooldown length (seconds) before this opponent can be re-challenged after a win. */
  cooldownSeconds: number;
  /** When this opponent is challengeable again (ISO) for the requesting character, or null. */
  cooldownUntil: string | null;
}

export interface NpcsResponse {
  npcs: NpcDto[];
}

/** Read-only loot odds for a single Sparring opponent — drives the loot-chance
 *  popup (the same dialog the Misdeeds / Good Deeds pages use). Built on demand
 *  so the opponent list stays light. */
export interface NpcLootResponse {
  /** Opponent name — popup heading. */
  title: string;
  /** Overall chance the gear roll fires on a WIN, 0..1. */
  dropChance: number;
  /** Enumerated weighted gear table. Per-item % = weight / sum(weight); each
   *  item's per-win odds = that share × dropChance. */
  dropTable: LootTableEntryDto[];
  /** Material pool tier this opponent rolls from on a WIN. */
  materialPoolTier: MaterialPoolTierDto;
  /** Chance (0..1) the material roll fires on a WIN (base, before House perks). */
  materialDropChance: number;
  /** Weighted material drop table for `materialPoolTier`. */
  materialDropTable: LootTableEntryDto[];
  /** Extra note rendered under the tables — e.g. the Morghul mythic line on the
   *  apex opponent. Null when there's nothing special to say. */
  mythicNote: string | null;
}

/** Lightweight "is there anything to collect?" signal for the sidebar glow.
 *  Each field is the completion timestamp (ISO) of an active, unclaimed
 *  activity, or null when nothing is running. The client lights the matching
 *  nav tab once `now >= readyAt`, projecting per-second like the resource glow. */
export interface ActivityStatusResponse {
  /** Active Scholarium enrollment's completion time, or null. */
  scholariumReadyAt: string | null;
  /** Active expedition's completion time, or null. */
  expeditionReadyAt: string | null;
  /** Earliest finish among active races, or null. */
  raceReadyAt: string | null;
}

export interface LoadoutResponse {
  loadout: LoadoutDto;
}

export interface EquipRequest {
  slot: EquipSlot;
  itemSlug: string;
}

export interface EquipResponse {
  character: CharacterDto;
  slot: EquipSlot;
  itemSlug: string;
  itemName: string;
  swappedOut: string | null;
}

export interface UnequipRequest {
  slot: EquipSlot;
}

export interface UnequipResponse {
  character: CharacterDto;
  slot: EquipSlot;
}

export type CombatOutcome = 'WIN' | 'LOSS' | 'DRAW';

export interface CombatRoundDto {
  index: number;
  firstActor: 'attacker' | 'defender';
  attacker: { hit: boolean; damage: number; lifeAfter: number };
  defender: { hit: boolean; damage: number; lifeAfter: number };
}

export interface AttackNpcRequest {
  npcSlug: string;
}

export interface AttackNpcResponse {
  character: CharacterDto;
  npcSlug: string;
  npcName: string;
  outcome: CombatOutcome;
  rounds: CombatRoundDto[];
  damageDealt: number;
  damageReceived: number;
  xpGained: number;
  /** BigInt as string. */
  crownsGained: string;
  /** Forge materials looted on WIN. */
  materialsDropped: Array<{ slug: string; name: string; qty: number }>;
  /** Equipment looted on WIN — rarity scales with the opponent. */
  lootDropped: Array<{ slug: string; name: string; qty: number; tier: string }>;
  hospitalisedUntil: string | null;
  levelsGained: number;
  /** When this opponent can be challenged again (ISO) — set after a WIN. */
  cooldownUntil: string | null;
}

// ──────────────────────────────────────────────────────────────────────────────
// PVP — Open 1v1 (M10)
// ──────────────────────────────────────────────────────────────────────────────

export type PvpOutcomeChoice = 'MERCY' | 'PLUNDER' | 'CRIPPLE';

export interface FindTargetRequest {
  name: string;
}

export interface FindTargetResponse {
  found: boolean;
  target: {
    id: string;
    name: string;
    level: number;
    location: string;
    status: string;
  } | null;
}

export interface AttackPlayerRequest {
  defenderName: string;
  outcome: PvpOutcomeChoice;
}

/** One player currently present at the viewer's location (the "Who's here" list
 *  on the Attacks page). Public-safe — never exposes crowns or stats. */
export interface PlayerHereDto {
  id: string;
  name: string;
  level: number;
  /** Live status: OKAY / INFIRMARY / DUNGEON. Travelling players are omitted. */
  status: string;
  activeTitleSlug: string | null;
  /** Whether the viewer may strike them right now. */
  attackable: boolean;
  /** Short reason shown when not attackable (e.g. "In the infirmary"). */
  reason: string;
}

export interface PlayersHereResponse {
  /** Location slug the viewer is at. */
  location: string;
  /** Display name of that location (e.g. "Rex Mortis", "Caledonia"). */
  locationName: string;
  players: PlayerHereDto[];
}

/** One typeahead suggestion for the Attacks name box. Public-safe. */
export interface TargetSuggestionDto {
  id: string;
  name: string;
  level: number;
  location: string;
  status: string;
}

export interface TargetSuggestionsResponse {
  suggestions: TargetSuggestionDto[];
}

export interface AttackPlayerResponse {
  character: CharacterDto;
  defenderId: string;
  defenderName: string;
  outcome: CombatOutcome;
  choice: PvpOutcomeChoice;
  rounds: CombatRoundDto[];
  damageDealt: number;
  damageReceived: number;
  xpGained: number;
  crownsPlundered: string;
  defenderHospitalUntil: string | null;
  attackerHospitalUntil: string | null;
  levelsGained: number;
  /** Renown the attacker's House earned from this win, broken down so the UI
   *  can show the bloodchain/war bonuses. Null when the attacker isn't in a
   *  House or the fight wasn't a win (no Renown awarded). */
  renown?: {
    /** Flat Renown for any in-House PvP win. */
    base: number;
    /** Bloodchain per-step bonus from rapid consecutive House wins (rolling
     *  5-min window, capped). */
    chainBonus: number;
    /** Extra Renown for striking a House you're at war with (0 in peacetime). */
    warBonus: number;
    /** One-shot bonus when this win lands exactly on a streak milestone
     *  (10, 25, 50, 100, 250, 500, 1000). 0 otherwise. */
    milestoneBonus: number;
    /** The House's true streak count *after* this win. */
    streakCount: number;
    /** base + chainBonus + warBonus + milestoneBonus. */
    total: number;
  } | null;
}

// ──────────────────────────────────────────────────────────────────────────────
// HOUSES (Factions, M11)
// ──────────────────────────────────────────────────────────────────────────────

export type HouseRank = 'INITIATE' | 'SQUIRE' | 'KNIGHT' | 'CHANCELLOR' | 'LORD';

export interface HouseSummaryDto {
  id: string;
  name: string;
  tag: string;
  motto: string;
  treasuryCrowns: string;
  renown: number;
  memberCount: number;
  createdAt: string;
  /** Banner image data URL, or null when the House uses the default art. */
  bannerUrl: string | null;
}

export interface HouseMemberDto {
  characterId: string;
  name: string;
  level: number;
  rank: HouseRank;
  joinedAt: string;
}

export interface HouseDetailDto extends HouseSummaryDto {
  members: HouseMemberDto[];
  myRank: HouseRank | null;
  /** Pending join applications — only non-zero for Chancellor+ viewers. */
  pendingApplicationCount: number;
}

export interface HousesIndexResponse {
  houses: HouseSummaryDto[];
}

export interface MyHouseResponse {
  house: HouseDetailDto | null;
  /** The viewer's pending join application, if any (only when not in a House). */
  application: MyApplicationDto | null;
}

// ── Public house view (read-only scouting card — never exposes treasury) ──────

export interface PublicHouseHoldingDto {
  slug: string;
  name: string;
  blurb: string;
  dailyRenown: number;
}

export interface PublicHouseDto {
  id: string;
  name: string;
  tag: string;
  motto: string;
  renown: number;
  memberCount: number;
  createdAt: string;
  bannerUrl: string | null;
  members: HouseMemberDto[];
  holdings: PublicHouseHoldingDto[];
}

export interface SetHouseBannerRequest {
  /** Resized banner image as a data URL. */
  bannerDataUrl: string;
}

export interface PublicHouseResponse {
  house: PublicHouseDto;
}

// ── House applications ────────────────────────────────────────────────────────

/** A pending application as seen by a reviewing officer. */
export interface HouseApplicationDto {
  id: string;
  characterId: string;
  name: string;
  level: number;
  message: string;
  appliedAt: string;
}

/** The viewer's own pending application. */
export interface MyApplicationDto {
  houseId: string;
  houseName: string;
  houseTag: string;
  appliedAt: string;
}

export interface ApplyHouseRequest {
  houseId: string;
  message?: string;
}

export interface ApplyHouseResponse {
  application: MyApplicationDto;
}

export interface HouseApplicationsResponse {
  applications: HouseApplicationDto[];
}

export interface ReviewApplicationRequest {
  applicationId: string;
  decision: 'APPROVE' | 'REJECT';
}

export interface CreateHouseRequest {
  name: string;
  tag: string;
  motto?: string;
}

export interface CreateHouseResponse {
  character: CharacterDto;
  house: HouseDetailDto;
}

export interface DepositTreasuryRequest {
  /** BigInt as string. */
  amount: string;
}

export interface DepositTreasuryResponse {
  character: CharacterDto;
  house: HouseDetailDto;
}

export interface SetRankRequest {
  targetCharacterId: string;
  newRank: HouseRank;
}

export interface KickRequest {
  targetCharacterId: string;
}

// ──────────────────────────────────────────────────────────────────────────────
// M14 — HOUSE PERK TREE
// ──────────────────────────────────────────────────────────────────────────────

/** Category groupings on the Perk Tree tab. Mirrors HousePerkCategory in the
 *  server catalog. */
export type HousePerkCategoryDto =
  | 'resources'
  | 'crime'
  | 'honour'
  | 'combat'
  | 'economy';

/** Describes how a perk's per-level number is applied. The client renders
 *  the display string from `kind` + the per-level bonus. */
export type HousePerkEffectDto =
  | { kind: 'bar_max'; bar: 'vigor' | 'resolve' | 'spirit' | 'life'; perLevel: number }
  | { kind: 'pct_bonus'; perLevel: number }
  | { kind: 'pct_discount'; perLevel: number }
  | { kind: 'flat_seconds'; perLevel: number };

/** One perk row on the Perk Tree tab — catalog metadata + the House's current
 *  level + the next-level cost preview. */
export interface HousePerkDto {
  slug: string;
  name: string;
  category: HousePerkCategoryDto;
  blurb: string;
  effect: HousePerkEffectDto;
  level: number;
  maxLevel: number;
  /** Cumulative bonus at the current level (0 when level === 0). */
  currentBonus: number;
  /** Cost to push to the next level. Null when already at maxLevel. */
  nextCost: { renown: number; crowns: number } | null;
  /** Bonus value AFTER buying the next level (null when maxed). */
  nextBonus: number | null;
}

export interface HousePerksResponse {
  perks: HousePerkDto[];
}

export interface BuyHousePerkRequest {
  perkSlug: string;
}

export interface BuyHousePerkResponse {
  perk: HousePerkDto;
  /** New House Renown after the purchase. */
  houseRenown: number;
  /** New House Treasury (BigInt as string). */
  houseTreasury: string;
}

// ──────────────────────────────────────────────────────────────────────────────
// M12 — WARS & HOLDINGS
// ──────────────────────────────────────────────────────────────────────────────

export type WarStatus = 'ACTIVE' | 'ATTACKER_WON' | 'DEFENDER_WON' | 'CANCELLED' | 'EXPIRED';

export interface WarDto {
  id: string;
  attackerHouseId: string;
  attackerName: string;
  attackerTag: string;
  defenderHouseId: string;
  defenderName: string;
  defenderTag: string;
  declaredAt: string;
  endsAt: string;
  status: WarStatus;
  attackerScore: number;
  defenderScore: number;
  respectThreshold: number;
  endedAt: string | null;
}

export interface WarsResponse {
  wars: WarDto[];
}

export interface DeclareWarRequest {
  defenderHouseId: string;
}

export interface HoldingDto {
  slug: string;
  name: string;
  blurb: string;
  dailyRenown: number;
  captureCost: number;
  ownerHouseId: string | null;
  ownerName: string | null;
  ownerTag: string | null;
  capturedAt: string | null;
}

export interface HoldingsResponse {
  holdings: HoldingDto[];
}

export interface ClaimHoldingRequest {
  holdingSlug: string;
}

// ──────────────────────────────────────────────────────────────────────────────
// M13 — AWARDS & BOONS
// ──────────────────────────────────────────────────────────────────────────────

export interface AwardDto {
  slug: string;
  name: string;
  blurb: string;
  category: string;
  achieved: boolean;
  achievedAt: string | null;
  boonReward: number;
}

export interface NewlyGrantedAwardDto {
  slug: string;
  name: string;
  boonReward: number;
}

export interface AwardsResponse {
  awards: AwardDto[];
  newlyGranted: NewlyGrantedAwardDto[];
}

export interface BoonShopEntryDto {
  slug: string;
  name: string;
  blurb: string;
  baseCost: number;
  maxRanks: number;
  ownedRanks: number;
  nextCost: number | null;
  /** When true, this boon has no rank cap (an infinite escalating-cost sink). */
  uncapped: boolean;
}

export interface BoonsResponse {
  boons: BoonShopEntryDto[];
  balance: number;
}

export interface BuyBoonRequest {
  boonSlug: string;
}

export interface BuyBoonResponse {
  character: CharacterDto;
  boonSlug: string;
  boonName: string;
  newRank: number;
  costPaid: number;
}

// ──────────────────────────────────────────────────────────────────────────────
// EXPEDITIONS
// Long-running quests (1-12 months) with rare-to-mythic loot.
// ──────────────────────────────────────────────────────────────────────────────

export type ExpeditionStatus = 'ACTIVE' | 'CLAIMED' | 'ABANDONED';

export interface ExpeditionCatalogEntry {
  slug: string;
  name: string;
  blurb: string;
  durationSeconds: number;
  levelGate: number;
  guaranteedCrowns: number;
  guaranteedXp: number;
  unlocked: boolean;
}

export interface ActiveExpeditionDto {
  id: string;
  slug: string;
  name: string;
  blurb: string;
  durationSeconds: number;
  startedAt: string;
  endsAt: string;
  remainingSeconds: number;
  ready: boolean;
}

export interface ExpeditionsResponse {
  catalog: ExpeditionCatalogEntry[];
  active: ActiveExpeditionDto | null;
}

export interface StartExpeditionRequest {
  expeditionSlug: string;
}

export interface StartExpeditionResponse {
  expedition: ActiveExpeditionDto;
}

export interface ExpeditionRewardDto {
  itemSlug: string;
  itemName: string;
  qty: number;
  tier: string;
}

export interface ClaimExpeditionResponse {
  character: CharacterDto;
  rewards: ExpeditionRewardDto[];
  crownsGained: string;
  xpGained: number;
  levelsGained: number;
}

// ──────────────────────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────────────────
// FORGE / CRAFTING
// ──────────────────────────────────────────────────────────────────────────────

export interface ForgeMaterialReqDto {
  slug: string;
  name: string;
  qty: number;
  /** How many the player currently has in inventory. */
  have: number;
}

export interface ForgeRecipeDto {
  slug: string;
  name: string;
  blurb: string;
  category: 'weapon' | 'armor' | 'consumable';
  materials: ForgeMaterialReqDto[];
  crowns: number;
  vigor: number;
  produces: { slug: string; name: string; tier: string };
  vocationSlug?: string | undefined;
  vocationLevel?: number | undefined;
  craftable: boolean;
  /** Plain-English reason why craftable is false (or null when true). */
  blockedReason: string | null;
}

export interface ForgeRecipesResponse {
  recipes: ForgeRecipeDto[];
}

export interface CraftRequest {
  recipeSlug: string;
  qty: number;
}

export interface CraftResponse {
  character: CharacterDto;
  recipeSlug: string;
  recipeName: string;
  crafted: number;
  produced: Array<{ slug: string; name: string; qty: number }>;
  consumed: Array<{ slug: string; name: string; qty: number }>;
  /** BigInt as string. */
  crownsSpent: string;
}

// ──────────────────────────────────────────────────────────────────────────────
// ITEM INFO — the dropdown info-panel surfaced from the Inventory page.
// Populated from the server's itemCatalog + itemMetadata + live DB counts.
// ──────────────────────────────────────────────────────────────────────────────

export interface ItemEffectDto {
  restores?: { vigor?: number; resolve?: number; spirit?: number; life?: number };
  cooldown?: 'elixir' | 'tonic' | 'medical' | 'food';
  cooldownSeconds?: number;
  overdoseChance?: number;
  removesInfirmary?: boolean;
  removesDungeon?: boolean;
}

export interface ItemPerksDto {
  battleStatPct?: number;
  lifestealPct?: number;
}

export interface ItemCirculationDto {
  /** Total qty held across all live characters' inventories. */
  totalQty: number;
  /** How many distinct characters currently hold ≥1. */
  owners: number;
}

export interface ItemInfoResponse {
  slug: string;
  name: string;
  blurb: string;
  category: string;
  tier: string;
  baseValue: number;
  stackable: boolean;
  attack: number | null;
  defense: number | null;
  accuracy: number | null;
  effect: ItemEffectDto | null;
  perks: ItemPerksDto | null;
  /** Long-form lore paragraph. Bespoke for ~50 hero items, blurb-fallback otherwise. */
  lore: string;
  /** True iff the lore is hand-written (vs. fell back to the catalog blurb). */
  loreIsBespoke: boolean;
  /** Where the item can be obtained (drops, shops, forge, etc.). */
  sources: string[];
  /** Short helper string summarising what this item does. */
  use: string;
  /** Crafting recipes that consume this item as an ingredient (empty for
   *  non-ingredients). Lets the info panel link out to what it's used for. */
  usedIn: ItemUsedInDto[];
  /** Live circulation snapshot. */
  circulation: ItemCirculationDto;
}

/** One recipe that uses an item as an ingredient. */
export interface ItemUsedInDto {
  /** The recipe's own slug. */
  recipeSlug: string;
  /** The recipe's display name. */
  recipeName: string;
  /** Slug of the item the recipe produces (base-quality output). */
  producesSlug: string;
  /** Display name of the produced item. */
  producesName: string;
  /** Which crafting house the recipe lives in (drives the link target). */
  house: 'forge' | 'apothecary' | 'cookfire' | 'brewhouse';
}

export interface ItemCirculationResponse {
  /** Map of slug → total quantity in circulation. */
  counts: Record<string, number>;
}

// ──────────────────────────────────────────────────────────────────────────────
// TITLES
// ──────────────────────────────────────────────────────────────────────────────

export type TitleTier = 'common' | 'rare' | 'legendary' | 'mythic' | 'ascended';

export interface TitleEntryDto {
  slug: string;
  text: string;
  /** The Award slug that unlocks this title (or undefined for default-unlocked). */
  awardSlug?: string;
  tier: TitleTier;
  /** Whether the calling character has unlocked this title. */
  earned: boolean;
}

export interface TitlesResponse {
  titles: TitleEntryDto[];
  activeTitleSlug: string | null;
}

// ──────────────────────────────────────────────────────────────────────────────
// REPLAYS — viewable AttackLog rows.
// ──────────────────────────────────────────────────────────────────────────────

export type ReplayOutcome = 'WIN' | 'LOSS' | 'DRAW' | 'ESCAPED';

export interface ReplaySummaryDto {
  id: string;
  attackerId: string;
  attackerName: string;
  defenderType: 'NPC' | 'CHARACTER';
  defenderRefId: string;
  defenderName: string;
  outcome: ReplayOutcome;
  rounds: number;
  damageDealt: number;
  damageReceived: number;
  xpGained: number;
  /** BigInt as string. */
  crownsGained: string;
  weaponUsed: string | null;
  /** ISO 8601. */
  attackedAt: string;
}

export interface ReplayDetailDto extends ReplaySummaryDto {
  /** Round-by-round combat log. Null for fights logged before the replay system. */
  roundsData: CombatRoundDto[] | null;
}

export interface ReplayListResponse {
  replays: ReplaySummaryDto[];
}

export interface ReplayDetailResponse {
  replay: ReplayDetailDto;
}

// ──────────────────────────────────────────────────────────────────────────────
// MEMBERSHIP (paid tiers)
// ──────────────────────────────────────────────────────────────────────────────

export type MembershipTierSlug = 'squire' | 'knight' | 'baron' | 'king';

export interface MembershipPerksDto {
  slug: MembershipTierSlug;
  name: string;
  blurb: string;
  barMultiplier: number;
  lifeMultiplier: number;
  bonusTokensPerDay: number;
  monthlyPriceCents: number;
  annualPriceCents: number;
  flairColour: string;
}

export interface MembershipStatusDto {
  /** Current tier or null when free. */
  tier: MembershipTierSlug | null;
  /** ISO expiry, null when free. */
  expiresAt: string | null;
  /** ISO start of current paid period, null when free. */
  startedAt: string | null;
  /** Convenience boolean: is this membership still in its paid window? */
  active: boolean;
}

export interface CreateOrderRequest {
  /** 'membership' or 'chest'. */
  productType: 'membership' | 'chest';
  /** Tier slug or chest slug. */
  productSlug: string;
  /** 'monthly' | 'annual' — only used for membership. */
  billingCycle?: 'monthly' | 'annual';
}

export interface CreateOrderResponse {
  /** Server-side order id we'll use to correlate the webhook. */
  orderId: string;
  /** PayPal order id created server-side. */
  paypalOrderId: string;
  /** Approval URL to redirect the user to. */
  approvalUrl: string;
  /** Echoed amount for client-side display. */
  amountCents: number;
  currency: string;
}

export interface CapturePaymentResponse {
  /** Final order status from PayPal. */
  status: string;
  /** Whether we successfully fulfilled the entitlement. */
  fulfilled: boolean;
  /** Updated membership status (if a membership was bought). */
  membership?: MembershipStatusDto;
  /** Slug of any chest dropped into inventory (if a chest was bought). */
  chestGranted?: string;
}

export interface ChestShopItemDto {
  slug: string;
  name: string;
  blurb: string;
  crowns: string; // BigInt-as-string
  priceCents: number;
}

// ──────────────────────────────────────────────────────────────────────────────
// REFILL TOKENS
// ──────────────────────────────────────────────────────────────────────────────

/** One bar's bucket state. */
export interface BarRefillStatusDto {
  /** Current tokens in this bar's bucket. */
  tokens: number;
  /** Max bucket size for this bar (cap on hoarding). */
  maxBucket: number;
  /** Seconds until the next +1 token rolls into this bucket. */
  secondsUntilNext: number;
}

export interface RefillStatusDto {
  vigor: BarRefillStatusDto;
  resolve: BarRefillStatusDto;
  spirit: BarRefillStatusDto;
  /**
   * Membership tier's daily token grant, added to EACH bar's bucket once per
   * day (0 if free). Informational only — the granted tokens are already folded
   * into the per-bar `tokens` counts above, so there is no separate free refill.
   */
  bonusTokensPerDay: number;
}

export interface RefillStatusResponse {
  status: RefillStatusDto;
}

/** Bars the refill system can top up. Life is deliberately excluded — it recovers via the Infirmary / combat flow. */
export type RefillableBar = 'vigor' | 'resolve' | 'spirit';

export interface UseRefillResponse {
  /** Updated character (the chosen bar now at max). */
  character: CharacterDto;
  /** Updated refill status. */
  status: RefillStatusDto;
  /** Always 'token' now — every refill spends a real bucket token (membership
   *  bonuses are deposited into the buckets, not spent as a separate grant). */
  source: 'token' | 'membership';
  /** Which bar was refilled. */
  bar: RefillableBar;
}

// ──────────────────────────────────────────────────────────────────────────────
// AVATAR LIBRARY
// ──────────────────────────────────────────────────────────────────────────────

export interface AvatarLibraryDto {
  /** All avatars the player owns (newest last). Each is a base64 data URL. */
  avatarLibrary: string[];
  /** Currently displayed avatar, or null for the default silhouette. */
  activeAvatarUrl: string | null;
  /** Slots free for a fresh upload (5 max). */
  slotsRemaining: number;
}

export interface AvatarLibraryResponse {
  library: AvatarLibraryDto;
}

export interface AvatarMutationResponse {
  character: CharacterDto;
  library: AvatarLibraryDto;
}

// ──────────────────────────────────────────────────────────────────────────────
// WIKI — auto-generated reference data for the in-game Compendium.
// Counts and tables are derived live from the server catalogs so the wiki never
// drifts from the real game. Prose lives in the client page; numbers come here.
// ──────────────────────────────────────────────────────────────────────────────

export interface WikiCounts {
  trainingTiers: number;
  sparringOpponents: number;
  crimes: number;
  goodDeeds: number;
  bankTiers: number;
  courses: number;
  trades: number;
  kingdoms: number;
  domicileTiers: number;
  domicileUpgrades: number;
  expeditions: number;
  recipes: number;
  ships: number;
  items: number;
  boons: number;
  awards: number;
  titles: number;
  events: number;
  dailyQuestKinds: number;
  holdings: number;
  gatheringSkills: number;
  gatheringSites: number;
}

export interface WikiBankTierDto {
  name: string;
  /** BigInt as string. */
  capCrowns: string;
  ratePerDayPct: number;
}

export interface WikiReference {
  counts: WikiCounts;
  /** Item rarity ladder, lowest → highest, display-cased. */
  rarityTiers: string[];
  /** Bank ladder for the Banks table. */
  bankTiers: WikiBankTierDto[];
}

export interface WikiReferenceResponse {
  reference: WikiReference;
}

// ──────────────────────────────────────────────────────────────────────────────
// ERROR ENVELOPE
// Every error response from the server has this exact shape.
// ──────────────────────────────────────────────────────────────────────────────

export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: Array<{ path: string; message: string }>;
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// PERK CATALOG — display names + effect text for every Scholarium perk.
// Single source of truth shared by the Wiki and the clickable perk popups.
// 'once' perks are granted one time on first completion; 'legacy' perks raise a
// stat cap EVERY time the course is re-claimed (and a few add a special effect).
// ──────────────────────────────────────────────────────────────────────────────

export interface PerkInfo {
  /** Display name, e.g. "Iron Grip". */
  name: string;
  /** Plain-language effect, shown in the popup + Wiki. */
  effect: string;
  /** 'once' = one-time unlock; 'legacy' = permanent cap raise per completion. */
  kind: 'once' | 'legacy';
  /** The course that grants it (for the Wiki). */
  source: string;
  /**
   * Set on legacy perks whose source course is itself one-shot (Pilgrimage,
   * Consecration, Apotheosis tiers). Repeatable-tier legacy perks
   * (Vigil, Season) stack across the first 5 completions; one-shot ones grant
   * the cap raise exactly once because the course can only be claimed once.
   * Affects the popup's "kind" label only.
   */
  oneShot?: boolean;
}

/** A Scholarium course's Legacy cap-raise applies only on the first
 *  LEGACY_MAX_STACKS_PER_COURSE completions of that course. Working-stat gains
 *  (Toil/Wits/Endurance) still apply on every completion, with no cap. */
export const LEGACY_MAX_STACKS_PER_COURSE = 5;

export const PERK_CATALOG: Record<string, PerkInfo> = {
  // One-time unlocks (Master / Royal courses).
  stronger_back:  { name: 'Stronger Back',  kind: 'once',   source: 'Master Stoneworker',          effect: 'Carry +5 extra items home when travelling abroad.' },
  shrewd_eye:     { name: 'Shrewd Eye',     kind: 'once',   source: 'Master Cartographer',         effect: '−1% fees on your Market sales (stacks with Long Memory, up to a −2% cap).' },
  hardy:          { name: 'Hardy',          kind: 'once',   source: 'Master Footman',              effect: '+50 to your maximum Life, permanently.' },
  cunning_writs:  { name: 'Cunning Writs',  kind: 'once',   source: 'Royal Tutor',                 effect: 'Misdeeds cost 10% less Resolve.' },
  pious_writs:    { name: 'Pious Writs',    kind: 'once',   source: 'Royal Almoner',               effect: 'Good deeds cost 10% less Resolve.' },
  iron_grip:      { name: 'Iron Grip',      kind: 'once',   source: 'Master-at-Arms',              effect: '+10% chance to break out of the Dungeon.' },

  // Legacy perks — the listed cap raise applies on your first 5 completions of
  // the course, then stops (working-stat gains keep applying with no cap).
  sweat_legacy:   { name: 'Sweat Legacy',   kind: 'legacy', source: 'Sworn Smithy',                effect: '+10 max Vigor per completion, for your first 5 completions.' },
  long_memory:    { name: 'Long Memory',    kind: 'legacy', source: 'Library Vigil',               effect: '+1 max Resolve per completion (first 5 completions); also −1% Market sale fees.' },
  iron_will:      { name: 'Iron Will',      kind: 'legacy', source: 'Iron Vigil',                  effect: '+25 max Life per completion, for your first 5 completions.' },
  master_hands:   { name: 'Master Hands',   kind: 'legacy', source: 'Master Builder',              effect: '+50 max Vigor per completion (first 5 completions); also +1 travel carry capacity.' },
  court_tongue:   { name: 'Court Tongue',   kind: 'legacy', source: 'Court Scholar',               effect: '+5 max Resolve per completion, for your first 5 completions.' },
  royal_command:  { name: 'Royal Command',  kind: 'legacy', source: 'Royal Guard Officer',         effect: '+100 max Life per completion, for your first 5 completions.' },
  master_of_works:{ name: 'Master of Works',kind: 'legacy', source: 'Master of the Stoneworks',    effect: '+200 max Vigor, granted on completion. (Course is once in a lifetime.)', oneShot: true },
  royal_routes:   { name: 'Royal Routes',   kind: 'legacy', source: 'Court Cartographer',          effect: '+10 max Resolve on completion; also −20% travel time (permanent). (Course is once in a lifetime.)', oneShot: true },
  pilgrim_steel:  { name: 'Pilgrim Steel',  kind: 'legacy', source: 'Pilgrim of the Sword',        effect: '+500 max Life, granted on completion. (Course is once in a lifetime.)', oneShot: true },
  crowned_master: { name: 'Crowned Master', kind: 'legacy', source: 'The Crowned Master',          effect: 'On completion: +2,000 Vigor, +100 Resolve, +1,000 Spirit, +5,000 Life (all max caps). Course is once in a lifetime.', oneShot: true },
  cathedral_mind: { name: 'Cathedral Mind', kind: 'legacy', source: 'Cathedral Scholar',           effect: 'On completion: +5,000 Vigor, +250 Resolve, +5,000 Spirit, +10,000 Life (all max caps). Course is once in a lifetime.', oneShot: true },
  cyclopean_arm:  { name: 'Cyclopean Arm',  kind: 'legacy', source: 'Cyclopean Smith',             effect: 'On completion: +5,000 Vigor, +100 Resolve, +2,500 Spirit, +20,000 Life (all max caps). Course is once in a lifetime.', oneShot: true },
  iron_pilgrim:   { name: 'Iron Pilgrim',   kind: 'legacy', source: 'Iron Pilgrim',                effect: 'On completion: +5,000 Vigor, +100 Resolve, +1,000 Spirit, +50,000 Life (all max caps). Course is once in a lifetime.', oneShot: true },
  god_among_men:  { name: 'God Among Men',  kind: 'legacy', source: 'God Among Men',               effect: 'On completion: +25,000 Vigor, +1,000 Resolve, +10,000 Spirit, +100,000 Life (all max caps). Course is once in a lifetime.', oneShot: true },
};

/** Display name for a perk slug, falling back to a title-cased slug. */
export function perkName(slug: string): string {
  return (
    PERK_CATALOG[slug]?.name ??
    slug.split('_').map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w)).join(' ')
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// HORSE RACING — the Racing Yard (stable, tracks, gear, races, rankings).
// ──────────────────────────────────────────────────────────────────────────────

export interface RaceStatsDto {
  speed: number;
  stamina: number;
  acceleration: number;
  grit: number;
}

export interface HorseGearMiniDto { slug: string; name: string; }

export interface HorseDto {
  id: string;
  horseSlug: string;
  breedName: string;
  name: string;
  tier: string;
  tierLabel: string;
  level: number;
  xp: string;
  xpIntoLevel: number;
  xpForNext: number;
  wins: number;
  races: number;
  surface: string;
  distance: string;
  blurb: string;
  stats: RaceStatsDto;
  baseStats: RaceStatsDto;
  gear: {
    saddle: HorseGearMiniDto | null;
    shoes: HorseGearMiniDto | null;
    barding: HorseGearMiniDto | null;
    charm: HorseGearMiniDto | null;
  };
  cooldownUntil: string | null;
  cooldownRemaining: number;
  racing: boolean;
  /** Listed for sale on the player market. */
  listed: boolean;
  /** Asking price (crowns) if listed, else null. */
  listPrice: string | null;
  /** What the NPC merchant would pay right now (crowns). */
  merchantBid: string;
}

export interface HorseMarketEntryDto {
  listingId: string;
  price: string;
  sellerName: string;
  isOwn: boolean;
  horse: HorseDto;
}

export interface HorseMarketResponse {
  listings: HorseMarketEntryDto[];
  /** Buyer's current crowns (for affordability UI). */
  canAfford: string;
}

export interface SellHorseMerchantResponse {
  stable: StableDto;
  crownsPaid: string;
}

export interface BuyHorseListingResponse {
  stable: StableDto;
  crownsPaid: string;
}

export interface StableDto {
  racingPrestige: number;
  stableSlots: number;
  usedSlots: number;
  nextSlotCost: number | null;
  maxStableSlots: number;
  renameCost: number;
  horses: HorseDto[];
}

export interface RaceTrackDto {
  slug: string;
  name: string;
  blurb: string;
  surface: string;
  distance: string;
  tierIndex: number;
  tierLabel: string;
  levelGate: number;
  entryFee: number;
  purse: number;
  durationSeconds: number;
  weights: RaceStatsDto;
  unlocked: boolean;
}

export interface ActiveRaceDto {
  id: string;
  horseId: string;
  horseName: string;
  trackSlug: string;
  trackName: string;
  startedAt: string;
  endsAt: string;
  remainingSeconds: number;
  ready: boolean;
}

export interface HorseGearDto {
  slug: string;
  name: string;
  slot: 'saddle' | 'shoes' | 'barding' | 'charm';
  tier: string;
  tierLabel: string;
  blurb: string;
  bonus: Partial<RaceStatsDto>;
  crowns: number;
  materials: Array<{ slug: string; name: string; qty: number; have: number }>;
  forgeable: boolean;
  blockedReason: string | null;
}

export interface RaceBreedDto {
  slug: string;
  name: string;
  tier: string;
  tierLabel: string;
  cost: number;
  prestigeRequired: number;
  levelGate: number;
  surface: string;
  distance: string;
  blurb: string;
  stats: RaceStatsDto;
}

export interface RacingYardResponse {
  stable: StableDto;
  tracks: RaceTrackDto[];
  active: ActiveRaceDto[];
  gear: HorseGearDto[];
  breeds: RaceBreedDto[];
}

export interface RacingRankingsResponse {
  topRiders: Array<{ characterId: string; name: string; racingPrestige: number; level: number }>;
  topHorses: Array<{ horseId: string; name: string; breedName: string; tierLabel: string; wins: number; races: number; ownerName: string; ownerId: string }>;
}

export interface BuyHorseResponse { stable: StableDto; horseId: string; }
export interface StableMutationResponse { stable: StableDto; }
export interface EnterRaceResponse { race: ActiveRaceDto; }

export interface ClaimRaceResponse {
  character: CharacterDto;
  placement: number;
  fieldSize: number;
  crownsGained: string;
  riderXpGained: number;
  levelsGained: number;
  horseXpGained: number;
  horseLeveledTo: number | null;
  prestigeGained: number;
  trackName: string;
  horseName: string;
  field: Array<{ name: string; ownerName: string; isGhost: boolean; isPlayer: boolean; placement: number }>;
}

// ------------------------------------------------------------------------------
// THE CRYPT (weekly co-op world boss)
// ------------------------------------------------------------------------------

export type CryptBossStatus = 'ACTIVE' | 'SLAIN' | 'EXPIRED';

/** One row of the contribution ladder shown on the Crypt page. */
export interface CryptContributorDto {
  rank: number;
  name: string;
  /** BigInt-as-string. */
  damage: string;
  /** 0..100, share of total damage dealt so far. */
  sharePct: number;
  /** True for the viewer's own row. */
  isYou: boolean;
}

export interface CryptStatusResponse {
  /** Null when no boss has ever spawned (shouldn't happen post-spawn-on-read). */
  boss: {
    bossSlug: string;
    name: string;
    blurb: string;
    /** Flavour image slug for the client (under /img/crypt/). */
    imageSlug: string;
    status: CryptBossStatus;
    maxHp: string;        // BigInt-as-string
    remainingHp: string;  // BigInt-as-string
    /** 0..100 — how much HP remains, for the bar. */
    hpPct: number;
    weekKey: string;
    spawnedAt: string;
    expiresAt: string;
    slainAt: string | null;
  } | null;
  /** The viewer's standing against the current boss. */
  you: {
    damage: string;       // BigInt-as-string
    sharePct: number;     // 0..100
    /** Vigor available to spend on a strike right now. */
    vigor: number;
    vigorMax: number;
    /** Estimated damage a full-Vigor strike would deal right now. */
    estStrikeDamage: string;
    /** True once rewards for a resolved boss have been collected. */
    claimed: boolean;
    /** True if there are unclaimed rewards from a resolved boss. */
    rewardReady: boolean;
  };
  /** Top contributors (capped server-side). */
  ladder: CryptContributorDto[];
  /** Total players who have struck this boss. */
  participantCount: number;
  /** This boss's full weighted loot table (shared pool + the boss's own
   *  signature drops), for the player-facing "spoils" popup. Loot only drops on
   *  a DEFEAT. */
  loot: CryptLootTableDto;
}

/** Weighted loot table for one Crypt boss, mirroring the Misdeeds/Good-Deeds
 *  loot popup shape. */
export interface CryptLootTableDto {
  /** Pool any contributor can roll. */
  contributor: {
    /** Drop chance at minimum (zero) share — the floor. 0..1. */
    dropChanceBase: number;
    /** Ceiling the per-roll drop chance rises to with damage share. 0..1. */
    dropChanceMax: number;
    /** Damage share at/above which a contributor drop is guaranteed. 0..1. */
    guaranteedShare: number;
    table: LootTableEntryDto[];
  };
  /** Pool only the single top-damage dealer can roll (artifact + mythic). */
  top: {
    dropChance: number; // 0..1
    table: LootTableEntryDto[];
  };
}

export interface CryptStrikeResponse {
  character: CharacterDto;
  /** Damage this strike dealt. BigInt-as-string. */
  damageDealt: string;
  /** Boss HP after the strike. BigInt-as-string. */
  remainingHp: string;
  hpPct: number;
  /** True if this strike (or the cumulative realm effort) felled the boss. */
  slain: boolean;
  /** Your new cumulative damage + share. */
  yourDamage: string;
  yourSharePct: number;
  /** Grave-spoils pried loose by the day's FIRST strike (instant materials,
   *  scaled to the Vigor spent). Empty on later strikes the same day. */
  spoils: Array<{ itemSlug: string; name: string; qty: number }>;
}

export interface CryptClaimRewardDto {
  /** crowns / xp / item. */
  kind: 'crowns' | 'xp' | 'item';
  /** For item drops: the item slug + name. */
  itemSlug?: string;
  label: string;
  /** Numeric amount (crowns/xp) or item qty. */
  amount: number;
}

export interface CryptClaimResponse {
  character: CharacterDto;
  rewards: CryptClaimRewardDto[];
  /** True if the viewer was the top-damage dealer for the boss. */
  wasTopDamage: boolean;
}

// ------------------------------------------------------------------------------
// THE SHADE (dark NPC that hunts the realm's strongest)
// ------------------------------------------------------------------------------

export interface DarkRaidStatusResponse {
  /** True while the viewer is marked and the window is still open. */
  marked: boolean;
  /** ISO — when the open mark lapses into a wound. Null when not marked. */
  windowEndsAt: string | null;
  /** Active battle wound, if any. */
  wound: {
    /** Percent reduction to every battle stat while wounded. */
    pct: number;
    /** ISO — when the wound fully heals. */
    healsAt: string;
  } | null;
}

export interface DarkRaidConfrontResponse {
  character: CharacterDto;
  /** 'WIN' | 'LOSS' from the player's perspective — the duel always resolves
   *  (at the round cap, the greater damage share carries it). */
  outcome: 'WIN' | 'LOSS' | 'DRAW';
  /** Set when the player prevailed. */
  xpGained: number;
  crownsGained: string;
  /** Set when the player was wounded (loss or draw). */
  wound: { pct: number; healsAt: string } | null;
}

// ── The Shade's Due (tribute) ────────────────────────────────────────────────

export interface ShadeTributeEntryDto {
  name: string;
  crowns: string;
  paidAt: string;
}

export interface TributeBoardResponse {
  /** ISO until when the viewer's ward holds; null when unwarded. */
  wardedUntil: string | null;
  /** True while the Shade has the viewer's scent — too late to pay. */
  marked: boolean;
  /** What the Due would cost the viewer right now. */
  costCrowns: string;
  /** The Roll of the Prudent — most recent payments first. */
  ledger: ShadeTributeEntryDto[];
}

export interface PayTributeResponse {
  character: CharacterDto;
  crownsPaid: string;
  wardedUntil: string;
}
